/**
 * pacAutoPopulation.js
 *
 * Intelligent cross-module clinical data aggregator for the Pre-Anesthesia
 * Assessment (PAC) form. Automatically harvests and normalizes patient data
 * across:
 *   1. IPD Admission & Demographics (Anthropometrics, Diagnosis, Surgeries)
 *   2. Nursing Module & Bed Dashboard (Latest Vitals, Consciousness, Dentures)
 *   3. Doctor Console IPD (Clinical Notes & Care Plans - Templates 8 & 2)
 *   4. Inpatient Pharmacy (Active Prescriptions)
 *   5. CPOE Diagnostic Systems (Lab Parameters: CBC, Chem, Coag & Radiology)
 *   6. Attending Doctor Directory (Cross-Specialty Referral Clearances)
 */

import axios from "axios";

const API_BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

export const getApiHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
  "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
  "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
  USERNAME: sessionStorage.getItem("userName") || "",
});

/** Safe array helper */
const toArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val && typeof val === "object") {
    if (Array.isArray(val.data)) return val.data;
    if (Array.isArray(val.orders)) return val.orders;
    if (Array.isArray(val.care_plans)) return val.care_plans;
    if (Array.isArray(val.results)) return val.results;
  }
  return [];
};

/**
 * Normalizes vital signs from nursing dashboard records.
 */
export function extractVitals(vitalRow = {}) {
  if (!vitalRow || typeof vitalRow !== "object") return {};

  let bpSys = "";
  let bpDia = "";
  const bpRaw = vitalRow.blood_pressure || vitalRow.bp || "";
  if (bpRaw) {
    const parts = String(bpRaw).split("/");
    if (parts[0]) bpSys = parts[0].trim();
    if (parts[1]) bpDia = parts[1].trim();
  }

  const pulse =
    vitalRow.heart_rate != null
      ? String(vitalRow.heart_rate)
      : vitalRow.pulse != null
      ? String(vitalRow.pulse)
      : "";

  const rr =
    vitalRow.respiratory_rate != null
      ? String(vitalRow.respiratory_rate)
      : vitalRow.rr != null
      ? String(vitalRow.rr)
      : "";

  const spo2 =
    vitalRow.oxygen_saturation != null
      ? String(vitalRow.oxygen_saturation)
      : vitalRow.spo2 != null
      ? String(vitalRow.spo2)
      : "";

  const temp =
    vitalRow.temperature != null
      ? String(vitalRow.temperature)
      : vitalRow.temp != null
      ? String(vitalRow.temp)
      : "";

  const weight =
    vitalRow.weight != null
      ? String(vitalRow.weight)
      : vitalRow.weight_kg != null
      ? String(vitalRow.weight_kg)
      : "";

  const height =
    vitalRow.height_cm != null
      ? String(vitalRow.height_cm)
      : vitalRow.height != null
      ? String(vitalRow.height)
      : "";

  let fio2 = "";
  if (vitalRow.o2_flow_lpm != null) {
    fio2 = String(vitalRow.o2_flow_lpm);
  } else if (vitalRow.fio2 != null) {
    fio2 = String(vitalRow.fio2);
  }

  // Consciousness mapping: Alert -> NORMAL, Confused -> CONFUSED, Drowsy -> DROWSY, Unresponsive -> OBTUNDED
  let mentalStatus = "NORMAL";
  const rawCons = String(vitalRow.consciousness || "").toUpperCase();
  if (rawCons.includes("CONFUS") || rawCons.includes("DELIR")) {
    mentalStatus = "CONFUSED";
  } else if (rawCons.includes("DROW") || rawCons.includes("LETHARG") || rawCons.includes("SEDAT")) {
    mentalStatus = "DROWSY";
  } else if (rawCons.includes("OBTUND") || rawCons.includes("COMA") || rawCons.includes("UNRESPONS")) {
    mentalStatus = "OBTUNDED";
  }

  return {
    bpSys,
    bpDia,
    pulsePerMin: pulse,
    rrPerMin: rr,
    spo2Percent: spo2,
    tempC: temp,
    weightKg: weight,
    heightCm: height,
    fio2Percent: fio2,
    neuroMental: mentalStatus,
  };
}

/**
 * Extracts clinical history, past surgeries, family history, and social habits
 * from Doctor Console IPD Care Plans (Template 8: Clinical Notes and Template 2: Note).
 */
export function extractClinicalHistory(carePlans = []) {
  const plans = toArray(carePlans);
  const out = {
    summaryHistory: "",
    previousSurgeries: [],
    familyAnesthComp: false,
    familyAnesthCompDesc: "",
    smokingPacks: "",
    smokingYears: "",
    smokingStopped: false,
    alcohol: "NIL",
    drugAbuse: "NIL",
    rawComorbidities: "",
    rawMedHistory: "",
    homeMedications: [],
  };

  const clinicalNotes = plans.filter((p) => {
    const tid = Number(p?.template_id || p?.care_plan_id);
    const sName = String(p?.structure_name || p?.type || "").toLowerCase();
    return tid === 8 || sName.includes("clinical") || sName.includes("clerking");
  });

  // Extract from the most recent or primary admission clerking note
  clinicalNotes.forEach((note) => {
    const d = note?.data || {};

    // 1. Narrative summary (HPI + Past Medical History + Comorbidities)
    const hpi = d.history_of_present_illness || d.hpi || "";
    const pmh = d.past_medical_history || d.pmh || "";
    const comorbidities = d.co_morbidities || d.comorbidities || "";

    out.rawComorbidities = [out.rawComorbidities, comorbidities].filter(Boolean).join("; ");
    out.rawMedHistory = [out.rawMedHistory, pmh].filter(Boolean).join("; ");

    const parts = [];
    if (hpi) parts.push(`HPI: ${hpi}`);
    if (pmh) parts.push(`Medical Hx: ${pmh}`);
    if (comorbidities) parts.push(`Comorbidities: ${comorbidities}`);

    if (parts.length > 0 && !out.summaryHistory) {
      out.summaryHistory = parts.join("\n\n");
    }

    // 2. Past surgical history table
    const surgeries = toArray(d.past_surgical_history || d.surgeries);
    surgeries.forEach((s, idx) => {
      const name = s?.surgery || s?.procedure || s?.name || s?.surgery_name || "";
      if (name) {
        out.previousSurgeries.push({
          id: Date.now() + idx,
          surgery_name: String(name).trim(),
          year: String(s?.date || s?.year || "").trim(),
          anesthesia_type: String(s?.anesthesia || s?.anesthesia_type || "General Anesthesia").trim(),
          complications: String(s?.remarks || s?.complications || "None").trim(),
        });
      }
    });

    // 3. Family history
    const fam = d.family_history || d.family_history_details || "";
    if (Array.isArray(fam)) {
      const famTexts = fam.map((f) => `${f.relation || "Relative"}: ${f.ailment || f.condition || ""}`).join(", ");
      if (/anesthesia|hyperthermia|paralysis|malignant|cardiac arrest|adverse/i.test(famTexts)) {
        out.familyAnesthComp = true;
        out.familyAnesthCompDesc = famTexts;
      }
    } else if (typeof fam === "string" && fam.trim()) {
      if (/anesthesia|hyperthermia|paralysis|malignant|cardiac arrest|adverse/i.test(fam)) {
        out.familyAnesthComp = true;
        out.familyAnesthCompDesc = fam.trim();
      }
    }

    // 4. Personal & Social History (Habits)
    const habits = String(d.personal_social_history || d.habits || "").toLowerCase();
    if (habits) {
      if (habits.includes("smok") || habits.includes("tobacco") || habits.includes("cigarette")) {
        out.smokingPacks = "1";
        if (habits.includes("quit") || habits.includes("stop") || habits.includes("ex-smok")) {
          out.smokingStopped = true;
        }
      }
      if (habits.includes("alcohol") || habits.includes("ethanol") || habits.includes("drink")) {
        if (habits.includes("heavy") || habits.includes("chronic") || habits.includes("regular")) {
          out.alcohol = "REGULAR";
        } else {
          out.alcohol = "OCCASIONAL";
        }
      }
      if (habits.includes("drug") || habits.includes("substance") || habits.includes("cannabis") || habits.includes("opioid")) {
        out.drugAbuse = "ACTIVE";
      }
    }

    // 5. Current medications from clinical note
    const meds = toArray(d.current_medications);
    meds.forEach((m) => {
      const name = m?.medicine_name || m?.name || "";
      if (name) {
        out.homeMedications.push(
          [name, m?.frequency, m?.quantity].filter(Boolean).join(" · ")
        );
      }
    });
  });

  return out;
}

/**
 * Normalizes active inpatient prescriptions and home medications into a unified list.
 */
export function extractActiveMedications(prescriptions = [], homeMedications = []) {
  const list = [];
  const seen = new Set();

  const rxRows = toArray(prescriptions);
  rxRows.forEach((rx) => {
    const name = String(rx?.medicine_name || rx?.generic_medicine_names || rx?.service || "").trim();
    if (!name || name === "—" || seen.has(name.toLowerCase())) return;
    seen.add(name.toLowerCase());

    const dose = rx?.dose || rx?.strength || "";
    const freq = rx?.frequency || "";
    const route = rx?.route || "";
    const status = rx?.status || "";

    const parts = [name];
    if (dose) parts.push(dose);
    if (route) parts.push(route);
    if (freq) parts.push(freq);
    if (status && status !== "Active") parts.push(`(${status})`);

    list.push(parts.join(" · "));
  });

  (homeMedications || []).forEach((m) => {
    const str = String(m || "").trim();
    if (str && !seen.has(str.toLowerCase())) {
      seen.add(str.toLowerCase());
      list.push(str);
    }
  });

  return list.join("\n");
}

/**
 * Normalizes allergies from OT case, nursing risk assessment, and admission record.
 */
export function extractAllergies(otCase = {}, nursingAssessment = {}, admission = {}) {
  const parts = [];

  const caseAllergies = String(otCase?.known_allergies || otCase?.allergies || "").trim();
  if (caseAllergies && !/^(nil|none|no|na|n\/a)$/i.test(caseAllergies)) {
    parts.push(caseAllergies);
  }

  const nurseAllergies = String(nursingAssessment?.allergies || "").trim();
  if (nurseAllergies && !/^(nil|none|no|na|n\/a)$/i.test(nurseAllergies)) {
    if (!parts.some((p) => p.toLowerCase().includes(nurseAllergies.toLowerCase()))) {
      parts.push(nurseAllergies);
    }
  }

  const admAllergies = admission?.allergies;
  if (Array.isArray(admAllergies)) {
    admAllergies.forEach((a) => {
      const s = String(a || "").trim();
      if (s && !/^(nil|none|no|na|n\/a)$/i.test(s) && !parts.some((p) => p.toLowerCase().includes(s.toLowerCase()))) {
        parts.push(s);
      }
    });
  } else if (typeof admAllergies === "string" && admAllergies.trim()) {
    if (!/^(nil|none|no|na|n\/a)$/i.test(admAllergies.trim()) && !parts.some((p) => p.toLowerCase().includes(admAllergies.trim().toLowerCase()))) {
      parts.push(admAllergies.trim());
    }
  }

  return parts.join(", ") || "None Known";
}

/**
 * Parses systemic review (ROS) clinical flags using medical terminology matching
 * across documented comorbidities and medical history.
 */
export function extractSystemicFlags(rawText = "") {
  const text = String(rawText || "").toLowerCase();
  const flags = {
    cvsHtn: false,
    cvsHtnStatus: "WELL_CONTROLLED",
    cvsIhd: false,
    cvsHf: false,
    cvsDyslipidemia: false,
    cvsPalpitation: false,
    respAsthma: false,
    respCopd: false,
    respRecentUri: false,
    respTb: false,
    respCough: false,
    respDyspnea: false,
    giGerd: false,
    giJaundice: false,
    giCirrhosis: false,
    renalCrf: false,
    renalDialysis: false,
    renalEsrd: false,
    endoDm: false,
    endoDmType: "TYPE_2",
    endoThyroid: "NORMAL",
    endoAdrenal: false,
    dentures: false,
  };

  if (!text) return flags;

  // Cardiovascular
  if (/hypertens|htn\b|high blood pressure|elevated bp/i.test(text)) {
    flags.cvsHtn = true;
    if (/uncontrolled|poorly controlled|crisis/i.test(text)) {
      flags.cvsHtnStatus = "POORLY_CONTROLLED";
    }
  }
  if (/\bihd\b|\bcad\b|coronary|angina|myocardial infarct|\bmi\b|stent|cabg/i.test(text)) {
    flags.cvsIhd = true;
  }
  if (/heart failure|\bchf\b|\bhf\b|congestive/i.test(text)) {
    flags.cvsHf = true;
  }
  if (/dyslipid|hyperlipid|cholesterol|hypercholester/i.test(text)) {
    flags.cvsDyslipidemia = true;
  }
  if (/palpitat|arrhythm|tachycard|atrial fib|\baf\b/i.test(text)) {
    flags.cvsPalpitation = true;
  }

  // Respiratory
  if (/asthma|reactive airway|bronchospasm/i.test(text)) {
    flags.respAsthma = true;
  }
  if (/\bcopd\b|emphysema|chronic bronchitis/i.test(text)) {
    flags.respCopd = true;
  }
  if (/\btb\b|tubercul/i.test(text)) {
    flags.respTb = true;
  }
  if (/cough/i.test(text)) {
    flags.respCough = true;
  }
  if (/dyspn|shortness of breath|\bsob\b|orthopn/i.test(text)) {
    flags.respDyspnea = true;
  }

  // GI & Hepatic
  if (/gerd\b|reflux|heartburn|hyperacid|acid peptic/i.test(text)) {
    flags.giGerd = true;
  }
  if (/jaundice|icterus|hyperbilirubin/i.test(text)) {
    flags.giJaundice = true;
  }
  if (/cirrhosis|portal hypertens|liver failure|chronic liver/i.test(text)) {
    flags.giCirrhosis = true;
  }

  // Renal
  if (/\bckd\b|\bcrf\b|renal disease|renal failure|chronic kidney/i.test(text)) {
    flags.renalCrf = true;
  }
  if (/dialysis|hemodialysis|peritoneal dial/i.test(text)) {
    flags.renalDialysis = true;
  }
  if (/\besrd\b|end stage renal/i.test(text)) {
    flags.renalEsrd = true;
  }

  // Endocrine
  if (/diabet|\bdm\b|\bt2dm\b|\bt1dm\b|hyperglyc/i.test(text)) {
    flags.endoDm = true;
    if (/\bt1dm\b|type 1/i.test(text)) {
      flags.endoDmType = "TYPE_1";
    } else if (/gestational/i.test(text)) {
      flags.endoDmType = "GESTATIONAL";
    }
  }
  if (/hypothyroid|hashimoto/i.test(text)) {
    flags.endoThyroid = "HYPOTHYROID";
  } else if (/hyperthyroid|graves|thyrotoxic/i.test(text)) {
    flags.endoThyroid = "HYPERTHYROID";
  }
  if (/adrenal|cushing|addison|steroid depend/i.test(text)) {
    flags.endoAdrenal = true;
  }

  // Dental
  if (/denture|false teeth|prosthesis dental/i.test(text)) {
    flags.dentures = true;
  }

  return flags;
}

/**
 * Extracts quantitative diagnostic test values (CBC, Chemistry, Coagulation)
 * from reported CPOE lab test orders and observations.
 */
export function extractDiagnosticLabParameters(labOrders = []) {
  const out = {
    cbcDate: "",
    cbcWbc: "",
    cbcHb: "",
    cbcHct: "",
    cbcPlt: "",
    chemDate: "",
    chemNa: "",
    chemK: "",
    chemCl: "",
    chemHco3: "",
    chemBun: "",
    chemCr: "",
    chemGlu: "",
    coagDate: "",
    coagPt: "",
    coagPtt: "",
    coagAptt: "",
    coagInr: "",
  };

  const orders = toArray(labOrders);
  orders.forEach((o) => {
    const sName = String(o?.service_name || o?.test_name || "").toLowerCase();
    const orderDate = String(o?.ordered_time || o?.created_at || "").substring(0, 10);
    const results = toArray(o?.results || o?.parameters || o?.test_results || o?.lab_results);

    // Flat test results row mapping
    results.forEach((r) => {
      const pName = String(r?.param || r?.parameter || r?.test_name || r?.test || "").toLowerCase();
      const val = String(r?.value != null ? r.value : r?.result != null ? r.result : "").trim();
      if (!val) return;

      // CBC / Hemogram
      if (sName.includes("cbc") || sName.includes("hemogram") || sName.includes("blood count")) {
        if (!out.cbcDate && orderDate) out.cbcDate = orderDate;
        if (/hemoglobin|\bhb\b/i.test(pName) && !out.cbcHb) out.cbcHb = val;
        else if (/wbc|tlc|leukocyte|white blood/i.test(pName) && !out.cbcWbc) out.cbcWbc = val;
        else if (/platelet|\bplt\b/i.test(pName) && !out.cbcPlt) out.cbcPlt = val;
        else if (/hematocrit|\bhct\b|\bpcv\b/i.test(pName) && !out.cbcHct) out.cbcHct = val;
      }

      // Renal Function / Biochemistry / Electrolytes
      if (sName.includes("kft") || sName.includes("rft") || sName.includes("renal") || sName.includes("electrolytes") || sName.includes("chemistry") || sName.includes("sugar") || sName.includes("glucose")) {
        if (!out.chemDate && orderDate) out.chemDate = orderDate;
        if (/sodium|\bna\b/i.test(pName) && !out.chemNa) out.chemNa = val;
        else if (/potassium|\bk\b/i.test(pName) && !out.chemK) out.chemK = val;
        else if (/chloride|\bcl\b/i.test(pName) && !out.chemCl) out.chemCl = val;
        else if (/bicarbonate|hco3/i.test(pName) && !out.chemHco3) out.chemHco3 = val;
        else if (/urea|\bbun\b/i.test(pName) && !out.chemBun) out.chemBun = val;
        else if (/creatinine|\bcr\b/i.test(pName) && !out.chemCr) out.chemCr = val;
        else if (/glucose|sugar|rbs|fbs/i.test(pName) && !out.chemGlu) out.chemGlu = val;
      }

      // Coagulation Profile
      if (sName.includes("coag") || sName.includes("pt") || sName.includes("inr") || sName.includes("aptt")) {
        if (!out.coagDate && orderDate) out.coagDate = orderDate;
        if (/\binr\b/i.test(pName) && !out.coagInr) out.coagInr = val;
        else if (/prothrombin time|\bpt\b/i.test(pName) && !out.coagPt) out.coagPt = val;
        else if (/aptt|partial thromboplastin/i.test(pName) && !out.coagAptt) out.coagAptt = val;
        else if (/ptt/i.test(pName) && !out.coagPtt) out.coagPtt = val;
      }
    });
  });

  return out;
}

/**
 * Extracts active cross-specialty referral clearances for this admission.
 */
export function extractReferrals(attendingDoctors = []) {
  const doctors = toArray(attendingDoctors);
  return doctors
    .filter((d) => d.role === "referral_in_ipd" || d.role === "Referral (In-IPD)")
    .map((d) => ({
      id: d.id || d.doctor_id,
      name: d.name,
      specialty: d.specialty || "Specialty Consultation",
      notes: d.notes || d.remarks || "Clearance requested via Doctor Console",
      date: d.created_at || new Date().toISOString(),
      status: "Clearance Requested",
    }));
}

/**
 * Master aggregator: performs concurrent parallel queries across IPD, Nursing,
 * Doctor Console, Pharmacy, and Diagnostic LIS/RIS modules, returning a unified
 * clinical dataset ready for PAC form consumption.
 */
export async function fetchClinicalDataForPac(admissionId, otCase = {}) {
  if (!admissionId) return null;

  const admStr = String(admissionId);
  const headers = getApiHeaders();

  const [
    vitalsRes,
    carePlansRes,
    prescriptionsRes,
    assessmentRes,
    doctorsRes,
    labRes,
    radRes,
  ] = await Promise.allSettled([
    // 1. Latest nursing vitals
    axios.get(`${API_BASE}nurse_dashboard/vitals/?admission_id=${admStr}&limit=1`, { headers }),
    // 2. Doctor care plans (Clinical Notes)
    axios.get(`${API_BASE}care_plans/?admission_id=${admStr}`, { headers }),
    // 3. Active inpatient prescriptions
    axios.post(`${API_BASE}get_ipd_prescriptions_by_admission/`, { admission_id: admStr }, { headers }),
    // 4. Nursing clinical assessment
    axios.get(`${API_BASE}nurse_dashboard/assessment/?admission_id=${admStr}&latest=1`, { headers }),
    // 5. Attending & referral doctors
    axios.get(`${API_BASE}ipd_admission/${admStr}/doctors/`, { headers }),
    // 6. CPOE Lab orders
    axios.post(`${API_BASE}get_ipd_lab_orders_by_admission/`, { admission_id: admStr }, { headers }),
    // 7. CPOE Radiology orders
    axios.post(`${API_BASE}get_ipd_radiology_orders_by_admission/`, { admission_id: admStr }, { headers }),
  ]);

  // Vitals
  const vitalsRaw =
    vitalsRes.status === "fulfilled"
      ? toArray(vitalsRes.value?.data?.data || vitalsRes.value?.data)[0] || {}
      : {};
  const normalizedVitals = extractVitals(vitalsRaw);

  // Care Plans / History
  const carePlansRaw =
    carePlansRes.status === "fulfilled"
      ? toArray(carePlansRes.value?.data?.data?.care_plans || carePlansRes.value?.data?.care_plans || carePlansRes.value?.data)
      : [];
  const normalizedHistory = extractClinicalHistory(carePlansRaw);

  // Prescriptions
  const prescriptionsRaw =
    prescriptionsRes.status === "fulfilled"
      ? toArray(prescriptionsRes.value?.data?.data || prescriptionsRes.value?.data?.orders || prescriptionsRes.value?.data)
      : [];
  const activeMedsText = extractActiveMedications(prescriptionsRaw, normalizedHistory.homeMedications);

  // Nursing assessment
  const assessmentRaw =
    assessmentRes.status === "fulfilled"
      ? assessmentRes.value?.data?.data || assessmentRes.value?.data || {}
      : {};
  const normalizedAllergies = extractAllergies(otCase, assessmentRaw, {});
  const denturesFlag = Boolean(assessmentRaw?.dentures || normalizedHistory.dentures);

  // Comorbidities & Systemic Review flags
  const combinedHistoryText = `${normalizedHistory.rawComorbidities} ${normalizedHistory.rawMedHistory} ${normalizedHistory.summaryHistory}`;
  const systemicFlags = extractSystemicFlags(combinedHistoryText);
  if (denturesFlag) systemicFlags.dentures = true;
  if (normalizedVitals.neuroMental) systemicFlags.neuroMental = normalizedVitals.neuroMental;

  // Lab parameters
  const labOrdersRaw =
    labRes.status === "fulfilled"
      ? toArray(labRes.value?.data?.orders || labRes.value?.data?.data?.orders || labRes.value?.data)
      : [];
  const normalizedLabParams = extractDiagnosticLabParameters(labOrdersRaw);

  // Radiology findings
  const radOrdersRaw =
    radRes.status === "fulfilled"
      ? toArray(radRes.value?.data?.orders || radRes.value?.data?.data?.orders || radRes.value?.data)
      : [];
  let cxrFindings = "";
  let ecgFindings = "";
  radOrdersRaw.forEach((r) => {
    const name = String(r?.service_name || r?.test_name || "").toLowerCase();
    const report = r?.findings || r?.impression || r?.results || "";
    if (typeof report === "string" && report.trim()) {
      if (/chest|cxr|lung/i.test(name)) cxrFindings = report.trim();
      else if (/ecg|ekg|echo/i.test(name)) ecgFindings = report.trim();
    }
  });

  // Referrals
  const attendingRaw =
    doctorsRes.status === "fulfilled"
      ? toArray(doctorsRes.value?.data?.doctors?.attending || doctorsRes.value?.data?.attending)
      : [];
  const activeReferrals = extractReferrals(attendingRaw);

  return {
    vitals: normalizedVitals,
    history: normalizedHistory,
    currentMedsText: activeMedsText,
    allergies: normalizedAllergies,
    systemicFlags,
    labParams: normalizedLabParams,
    cxrFindings,
    ecgFindings,
    activeReferrals,
    rawCounts: {
      labOrders: labOrdersRaw.length,
      radOrders: radOrdersRaw.length,
      prescriptions: prescriptionsRaw.length,
      carePlans: carePlansRaw.length,
      referrals: activeReferrals.length,
    },
  };
}
